import type { TimelineItem } from "@/models/legislation/Timeline";
import { DOCUMENT_TYPE_ID_BEKENDTGØRELSE } from "@/utils/legislationDocumentTypeUtils";
import { parseSignatureDate } from "@/lib/timelineDates";

/**
 * A BEK version (type 60) forms the spine/branches of a bekendtgørelse timeline.
 * Anything else on the tree (amendment BEKs, type 80) is an inline change applied
 * to whatever branch its anchor is on.
 */
const isBekVersion = (node: TimelineItem): boolean =>
  Number(node.documentTypeId) === DOCUMENT_TYPE_ID_BEKENDTGØRELSE;

/** Sortable timestamp for a timeline node (signature date, falling back to year). */
const amendmentSortValue = (node: TimelineItem): number => {
  const parsed = parseSignatureDate(node.signatureDate);
  if (parsed) return parsed.getTime();
  const year = node.year && node.year > 0 ? node.year : 9999;
  return new Date(year, 0, 1).getTime();
};

/**
 * Collect, for a single BEK version, its inline amendments and the BEK versions
 * that continue the spine/branch.
 *
 * Amendments (non-60) belong inline on the current branch. In the final data
 * shape they are leaves; today they can still carry the next BEK version as a
 * child, so we descend *through* amendments to lift any nested BEK versions back
 * onto the spine (and treat any nested non-60s as further inline amendments).
 */
const collectSegment = (
  children: TimelineItem[] | undefined,
  amendments: TimelineItem[],
  nextBeks: TimelineItem[]
): void => {
  for (const child of children ?? []) {
    if (isBekVersion(child)) {
      nextBeks.push(child);
    } else {
      amendments.push(child);
      collectSegment(child.children, amendments, nextBeks);
    }
  }
};

/**
 * Reshape a delivered bekendtgørelse timeline tree so amendment BEKs (type 80)
 * are inline nodes on their anchor's branch (a single-child chain), while genuine
 * legal splits (a BEK version with two or more BEK-version descendants) remain
 * branches. Amendments are ordered by signature date immediately after their
 * anchor version; the next BEK version(s) follow.
 */
export const normalizeBekTimelineTree = (root: TimelineItem): TimelineItem => {
  const amendments: TimelineItem[] = [];
  const nextBeks: TimelineItem[] = [];
  collectSegment(root.children, amendments, nextBeks);

  amendments.sort((a, b) => amendmentSortValue(a) - amendmentSortValue(b));

  const normalizedNexts = nextBeks.map(normalizeBekTimelineTree);

  const continuation: TimelineItem[] =
    normalizedNexts.length === 0
      ? []
      : normalizedNexts.length === 1
        ? [normalizedNexts[0]]
        : normalizedNexts;

  // Build head -> amend1 -> ... -> amendK -> continuation as a chain of shallow
  // copies, overriding only `children`. The continuation hangs off the last
  // amendment (or the head when there are no amendments); >=2 nexts is a branch.
  const chainNodes: TimelineItem[] = [
    { ...root },
    ...amendments.map((a) => ({ ...a })),
  ];

  for (let i = 0; i < chainNodes.length; i++) {
    const isTail = i === chainNodes.length - 1;
    chainNodes[i].children = isTail ? continuation : [chainNodes[i + 1]];
  }

  return chainNodes[0];
};

/**
 * Normalize a timeline for display. A bekendtgørelse timeline is a length-1 array
 * whose single BEK (60) root carries a nested `children` tree; it is normalized so
 * amendments render inline and only genuine splits branch. Law timelines (flat
 * arrays) are already linear and returned unchanged.
 */
export const normalizeTimelineItems = (
  items: TimelineItem[] | undefined
): TimelineItem[] => {
  if (!items?.length) return items ?? [];
  if (items.length === 1 && items[0].children?.length) {
    return [normalizeBekTimelineTree(items[0])];
  }
  return items;
};
